/**
 * AssetRequirementsEditor — Multi-asset requirement rows for job creation/editing.
 * When the `multi_asset_jobs` flag is on, this replaces the single Asset Type dropdown.
 * Each row: equipment type picker + quantity stepper + remove button.
 */
import { useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Check, ChevronDown, Plus, Minus, X } from 'lucide-react';

export default function AssetRequirementsEditor({ requirements, onChange, equipmentTypes }) {
  const [openIdx, setOpenIdx] = useState(null);

  const updateReq = (idx, field, value) => {
    const next = requirements.map((r, i) => i === idx ? { ...r, [field]: value } : r);
    onChange(next);
  };

  const addRow = () => {
    onChange([...requirements, { equipment_type: '', quantity: 1 }]);
  };

  const removeRow = (idx) => {
    if (requirements.length <= 1) return;
    onChange(requirements.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-3" data-testid="asset-requirements-editor">
      <Label className="label-overline">Asset Requirements <span className="text-red-500">*</span></Label>
      {requirements.map((req, idx) => (
        <div key={idx} className="flex items-center gap-2" data-testid={`asset-req-row-${idx}`}>
          {/* Type picker */}
          <div className="flex-1 min-w-0">
            <Popover open={openIdx === idx} onOpenChange={(v) => setOpenIdx(v ? idx : null)} modal>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between font-normal text-sm"
                  data-testid={`asset-req-type-${idx}`}
                >
                  {req.equipment_type
                    ? <span className="truncate">{req.equipment_type}</span>
                    : <span className="text-muted-foreground">Select asset type...</span>}
                  <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search asset types..." />
                  <CommandList className="max-h-[250px] overflow-y-auto">
                    <CommandEmpty>No asset type found.</CommandEmpty>
                    <CommandGroup>
                      {equipmentTypes.map(t => (
                        <CommandItem
                          key={t}
                          value={t}
                          onSelect={() => { updateReq(idx, 'equipment_type', t); setOpenIdx(null); }}
                        >
                          <Check className={`mr-2 h-3.5 w-3.5 ${req.equipment_type === t ? 'opacity-100' : 'opacity-0'}`} />
                          {t}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Quantity stepper */}
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              className="h-8 w-8 flex items-center justify-center rounded-md border border-border hover:bg-accent disabled:opacity-30 transition-colors"
              onClick={() => updateReq(idx, 'quantity', Math.max(1, req.quantity - 1))}
              disabled={req.quantity <= 1}
              data-testid={`asset-req-qty-minus-${idx}`}
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="w-8 text-center text-sm font-medium tabular-nums" data-testid={`asset-req-qty-${idx}`}>
              {req.quantity}
            </span>
            <button
              type="button"
              className="h-8 w-8 flex items-center justify-center rounded-md border border-border hover:bg-accent transition-colors"
              onClick={() => updateReq(idx, 'quantity', req.quantity + 1)}
              data-testid={`asset-req-qty-plus-${idx}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Remove (not for first row) */}
          <button
            type="button"
            className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-0 disabled:pointer-events-none"
            onClick={() => removeRow(idx)}
            disabled={requirements.length <= 1}
            data-testid={`asset-req-remove-${idx}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={addRow}
        data-testid="asset-req-add-btn"
      >
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Add Asset Type
      </Button>

      {/* Summary */}
      {requirements.length > 0 && requirements.some(r => r.equipment_type) && (
        <p className="text-xs text-muted-foreground">
          {requirements.reduce((sum, r) => sum + (r.equipment_type ? r.quantity : 0), 0)} total asset slot{requirements.reduce((s, r) => s + (r.equipment_type ? r.quantity : 0), 0) !== 1 ? 's' : ''} across {requirements.filter(r => r.equipment_type).length} type{requirements.filter(r => r.equipment_type).length !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}
